import { Box, Button, Grid, Link } from "@mui/material";
import { useContext, useState, useRef, useEffect } from "react";

// components
import SamplesFilter from "./SamplesFilter";
import SampleMenuList from "./SampleMenuList";
import MenuContext from "../../store/menu-context";
import CustomLink from "../Common/CustomLink";
import LineSvg from "../LineSvg";
import DottedCircleSvg from "../DottedCircleSvg";
import Glyphosate from "./Glyphosate";
import ImportControl from "./ImportControl";
import CircleSvg from "../CircleSvg";
import AnimalProductsFish from "./AnimalProductsFish";
import AnimalProducts from "./AnimalProducts";
import SamplingRegion from "./SamplingRegion";
import PesticideResiduesResults from "./PesticideResiduesResults";
import ProcessedFoodProducts from "./ProcessedFoodProducts";
import OrganicFoodProducts from "./OrganicFoodProducts";
import BabyFood from "./BabyFood";
import ModalTrigger from "../Common/ModalTrigger";

// maps
import ReportingCountryMap from "../Maps/ReportingCountryMap";
import mapJSON from "../../Data/JSON/ChapterOneMap.json";
import mapCSV from "../../Data/MANCP/Geospatial/MANCP-Reporting-country.csv";

import classes from "../../Style/ReportingCountry.scss";
import {useNavigate} from "react-router-dom";
import PageRoutes from "../../Config/PageRoutes";

const ReportingCountry = () => {
  const anchorOne = useRef(null);
  const anchorTwo = useRef(null);
  const anchorThree = useRef(null);
  const navigate = useNavigate();

  const goToChapterThree = () => {
    navigate("../" + PageRoutes.CHAPTER_THREE);
  };
  const filters = [
    "Number of samples",
    "Number of samples per 100k inhabitants",
    "Difference in samples from past year (%)",
    "Rate of samples below LOQ",
    "Rate of samples between LOQ and MRL",
    "Rate of quantification",
    "Rate of non-compliant samples",
    "Rate of samples above legal limit",
  ];

  const [samplesFilter, setSamplesFilter] = useState(filters[0]);

  const handleFilterChange = (evt) => {
    setSamplesFilter(evt);
  };

  const ctx = useContext(MenuContext);
  if (!ctx.scrollEnabled && anchorOne.current !== null) {
    if (ctx.index === 0) {
      window.scrollTo({
        top: anchorOne.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    } else if (ctx.index === 1) {
      window.scrollTo({
        top: anchorTwo.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    } else if (ctx.index === 2) {
      window.scrollTo({
        top: anchorThree.current.offsetTop - 100,
        left: 0,
        behavior: "smooth",
      });
    }
  }

  const handleScroll = (e) => {
    const scrollTop = e.srcElement.firstElementChild.scrollTop;
    if (
      anchorOne.current !== null &&
      scrollTop >= 0 &&
      scrollTop < anchorOne.current.offsetTop
    ) {
      ctx.updateContext(0, true);
    } else if (
      anchorTwo.current !== null &&
      scrollTop >= anchorTwo.current.offsetTop - 100 &&
      scrollTop < anchorThree.current.offsetTop - 100
    ) {
      ctx.updateContext(1, true);
    } else if (
      anchorThree.current !== null &&
      scrollTop >= anchorThree.current.offsetTop - 100
    ) {
      ctx.updateContext(2, true);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", (e) => handleScroll(e));
    return () => {
      window.removeEventListener("scroll", (e) => handleScroll(e));
    };
  }, []);

  return (
    <Grid container direction="column" className="outer-wrapper-reporting">
      <Grid container direction="column" className="reportingCountryWrapper">
        {/* First row */}

        <Grid
          item
          container
          ref={anchorOne}
          xs
          direction={{ xs: "column", sm: "column", md: "row" }}
          className="reportingCountryFindings"
        >
          <Grid item xs p={3} flexDirection="column">
            <Box display="flex" flexDirection="column">
              <div className="lineSvg">
                <LineSvg />
              </div>

              <span>MANCP RESULTS</span>
              <h3>Reporting country findings</h3>
              <p className="paragraph">
                In the <ModalTrigger
                  style={{
                    color: "#22294D",
                    cursor: "pointer",
                    borderBottom: "1px dotted",
                  }}
                  text="context of the Multiannual National Control Programme (MANCP)"
                />,{" "}
                <ModalTrigger
                  style={{
                    color: "#22294D",
                    cursor: "pointer",
                    borderBottom: "1px dotted",
                  }}
                  text="EU Member States"
                />
                , Iceland and Norway analysed a total of 86,449 samples of food products covered by
                <CustomLink
                  text=" Regulation (EC) No 396/2005"
                  href="https://eur-lex.europa.eu/legal-content/EN/TXT/PDF/?uri=CELEX:32005R0396&from=EN"
                />{" "}
                for pesticide residues in 2024. Additionally, 11 countries reported 1,442 feed samples and 21 countries reported 1,063 fish samples.
              </p>
              <p className="paragraph">
                Of the total 86,449 samples analysed, 58.4% (50,524) contained residues below the{" "}
                <ModalTrigger
                  style={{
                    color: "#22294D",
                    cursor: "pointer",
                    borderBottom: "1px dotted",
                  }}
                  text="LOQ"
                />
                , while 38.3% (33,067 samples) contained residues{" "}
                <ModalTrigger
                  style={{
                    color: "#22294D",
                    cursor: "pointer",
                    borderBottom: "1px dotted",
                  }}
                  text="at or above the LOQ but below or equal to the MRL"
                />
                . MRL exceedances were detected in 3.3% of samples (2,858), of which 1.8% (1,591 samples) were found to be{" "}
                <ModalTrigger
                  style={{
                    color: "#22294D",
                    cursor: "pointer",
                    borderBottom: "1px dotted",
                  }}
                  text="non-compliant"
                />.
              </p>
            </Box>

            <Box
              sx={{
                display: { xs: "none", sm: "none", md: "block" },
                maxWidth: "400px",
              }}
            >
              <SamplesFilter
                title="Number of samples"
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Box>

            <Box sx={{ display: { xs: "block", sm: "block", md: "none" } }}>
              <span style={{ fontSize: "14px" }}>Filter by</span>
              <SampleMenuList
                title="Number of samples"
                filters={filters}
                onFilterChange={handleFilterChange}
              />
            </Box>
          </Grid>
          <Grid item xs p={3} className="one-two">
            <ReportingCountryMap
              csv={mapCSV}
              samplesFilter={samplesFilter}
              json={mapJSON}
            />
          </Grid>
        </Grid>
        {/* First row */}

        {/* Second row */}
        <Grid>
          <div className="circle-svg">
            <CircleSvg />
          </div>
          <SamplingRegion />
        </Grid>
        {/* Second row */}

        {/* Third row */}
        <Grid ref={anchorTwo}>
          <PesticideResiduesResults />
        </Grid>
        {/* Third row */}

        {/* Fourth row */}
        <Glyphosate />
        {/* Fourth row */}

        {/* Fifth row */}
        <ImportControl />
        {/* Fifth row */}

        {/* Sixth row */}
        <Grid ref={anchorThree}>
          <ProcessedFoodProducts />
        </Grid>
        {/* Sixth row */}

        {/* Seventh row */}
        <Grid>
          <OrganicFoodProducts />
        </Grid>
        {/* Seventh row */}

        {/* Eighth row */}
        <Grid>
          <BabyFood />
        </Grid>
        {/* Eighth row */}

        {/* Ninth row */}
        <Grid>
          <AnimalProducts />
        </Grid>
        {/* Ninth row */}

        {/* Tenth row */}
        {/* <Grid>
          <AnimalProductsFish />
        </Grid> */}
        {/* Tenth row */}

        {/* Eleventh row, footer */}
        <Grid
          item
          container
          direction="column"
          alignContent="center"
          className="footerWrapper"
        >
          <div className="dottedCircle">
            <DottedCircleSvg />
          </div>
          <Grid item xs className="footer">
            <Box className="nine-one-inner">
              <p style={{ textAlign: "center" }}>
                <b>Continue exploring</b>
              </p>
              <Button
                className="footerButton"
                variant="outlined"
                component={Link}
                onClick={goToChapterThree}
              >
                INCREASED IMPORT CONTROL PROGRAMME
              </Button>
            </Box>
          </Grid>
        </Grid>
        {/* Eleventh row */}
      </Grid>
    </Grid>
  );
};

export default ReportingCountry;
